import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFileExplorerContext } from './useFileExplorerContext';
import { getFiles, TreeFolder } from '../components/FileExplorer/services';
import { PathNode } from '../components/FileExplorer/Context/FileExplorerContext';
import { folderKeys } from '../components/FileExplorer/utils/queryKeys';

export function useFileExplorerFolder({
  id, name, parentPath = [], permissions, shareable_link,
}: TreeFolder & { parentPath?: PathNode[] }) {
  const {
    activeFolder, setActiveFolder, expandedFolderIds, toggleFolderExpanded,
  } = useFileExplorerContext();

  const isExpanded = expandedFolderIds.has(id);

  // Check if this folder is currently active using the ID from context
  const isActive = activeFolder?.id === id;
  const {
    data: folderData, isLoading, isError, isInitialLoading, error, isFetching, status,
  } = useQuery({
    queryKey: folderKeys.detail(id),
    queryFn: () => getFiles({ id }),
    // Only fetch when expanded to avoid unnecessary API calls
    enabled: isExpanded || isActive,
  });

  const isFetched = status === 'success';

  const handleToggle = useCallback(() => {
    const newPath = [...parentPath, { id, name }];

    // Set this folder as active by updating context with ID, path, and metadata
    // Actual folder content will be fetched via React Query using useActiveFolderData hook
    // But we store metadata (permissions, shareable_link) since it's not in the content response
    setActiveFolder({
      id,
      path: newPath,
      metadata: {
        id, name, permissions, shareable_link,
      },
    });
    toggleFolderExpanded(id);
  }, [id, name, parentPath, permissions, shareable_link, setActiveFolder, toggleFolderExpanded]);

  return {
    isExpanded,
    handleToggle,
    folders: folderData?.data.folders,
    files: folderData?.data.files,
    isLoading,
    isFetched,
    isError,
    isInitialLoading,
    isFetching,
    error,
    isActive,
  };
}
